import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  TextField,
  Autocomplete,
  Box,
  Card,
  Grid,
  styled,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FilledInput
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';
import SaveIcon from '@mui/icons-material/Save';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  AddOnCategory,
  AddOnItem,
  Printer
} from '../../../../services/apiService/response-models';
import DropdownMultiSelect from 'src/components/dropdown-multi-select';
import { getAllAddOnCategories, getPrinterList } from '../../../../services/apiService';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import { useNavigate } from 'react-router-dom';

const EditorWrapper = styled(Box)(
  ({ theme }) => `

    .ql-editor {
      min-height: 100px;
    }

    .ql-snow .ql-picker {
      color: ${theme.colors.alpha.black[100]};
    }

    .ql-snow .ql-stroke {
      stroke: ${theme.colors.alpha.black[100]};
    }

    .ql-toolbar.ql-snow {
      border-top-left-radius: ${theme.general.borderRadius};
      border-top-right-radius: ${theme.general.borderRadius};
    }

    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${theme.colors.alpha.black[30]};
    }

    .ql-container.ql-snow {
      border-bottom-left-radius: ${theme.general.borderRadius};
      border-bottom-right-radius: ${theme.general.borderRadius};
    }

    &:hover {
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: ${theme.colors.alpha.black[50]};
      }
    }
`
);
const CopyContentIcon = styled(ContentCopyTwoToneIcon)(
  ({ theme }) => `
    margin-left: ${theme.spacing(1)};
    cursor: pointer;
    color: ${theme.palette.primary.main};
    font-size: 30px;
  `
);

function GeneralSection(props: {
  model: Printer | null;
  isSaveInProgress: boolean;
  onSaveClick: () => void;
  onSave: (updatedModel: Printer) => Promise<boolean>;
}) {
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate();

  const [isEdit] = React.useState(props.model != null && props.model.id);
  const [categories, setCategories] = useState<Printer[]>([]);
  const deviceTypes = ['POS', 'Kiosk', 'server-go'];
  const getByCategories = async () => {
    try {
      const response = await getPrinterList();
      setCategories(response.data.filter((o) => o.isActive));
    } catch (ex) {
      console.error(ex);
    }
  };

  useEffect(() => {
    getByCategories();
  }, []);

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Card
      sx={{
        p: 3
      }}
    >
      <Formik
        initialValues={{
          name: props.model?.name || '',
          description: props.model?.description || '',
          macAddress: props.model?.macAddress || '',
          ipAddress: props.model?.ipAddress || '',
          port: props.model?.port?.toString() || '',
          categoryIds: props.model?.id,
          driver: Array.isArray(props.model?.driver) ? props.model.driver : (props.model?.driver ? [props.model.driver] : []),
          isActive: isEdit ? props.model?.isActive === true : true
        }}

        validationSchema={Yup.object().shape({
          name: Yup.string().max(250).required(t('The name field is required')),
          ipAddress: Yup.string().max(250).required(t('The IP adress is required')),
        })}

        onSubmit={async (
          _values,
          { resetForm, setErrors, setStatus, setSubmitting }
        ) => {
          try {
            console.log(_values);
            var isSuccess = await props.onSave({
              ...(props.model || {}),
              ..._values,
              driver: _values.driver,
              id: props.model?.id || '',
              tenantCode: props.model?.tenantCode || '',
              createdOn: props.model?.createdOn || new Date(),
            });
            if (isSuccess) {
              resetForm();
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err) {
            console.error(err);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  placeholder={t('Enter printer name...')}
                  variant="outlined"
                  label="Printer Name"
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="macAddress"
                  placeholder={t('Enter macAddress ...')}
                  variant="outlined"
                  label="Mac Address"
                  error={Boolean(touched.macAddress && errors.macAddress)}
                  helperText={touched.macAddress && errors.macAddress}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.macAddress}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="ipAddress"
                  placeholder={t('Enter IP Address...')}
                  variant="outlined"
                  label="IP Address"
                  error={Boolean(touched.ipAddress && errors.ipAddress)}
                  helperText={touched.ipAddress && errors.ipAddress}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.ipAddress}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="port"
                  placeholder={t('Enter port...')}
                  variant="outlined"
                  label="Port"
                  error={Boolean(touched.port && errors.port)}
                  helperText={touched.port && errors.port}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.port}
                />
              </Grid>


              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  placeholder={t('Enter description...')}
                  variant="outlined"
                  label="Description"
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline={true}
                  rows={3}
                  value={values.description}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  disablePortal
                  freeSolo
                  options={deviceTypes}
                  value={values.driver}
                  onChange={(event, newValue) => {
                    setFieldValue('driver', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="driver"
                      label={t('Driver')}
                      variant="outlined"
                      onBlur={handleBlur}
                      error={Boolean(touched.driver && errors.driver)}
                      helperText={touched.driver && errors.driver}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      name="isActive"
                      disabled={!isEdit}
                      color={isEdit ? 'primary' : 'secondary'}
                      onBlur={handleBlur}
                      checked={values.isActive}
                      onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    />
                  }
                  label={t('Is Active')}
                />

              </Grid>

              <Grid item xs={12}>
                <Button
                  sx={{ mr: 2 }}
                  type="button"
                  onClick={goBack}
                  variant="text"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={() => props.onSaveClick()}
                  variant="contained"
                  startIcon={
                    isSubmitting || props.isSaveInProgress ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isSubmitting || props.isSaveInProgress}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Card>
  );
}

export default GeneralSection;
